import { IS_NULL_OR_UNDEFINED, IS_EMPTY, IS_EMPTY_LIST } from 'cargo-chief-commons';
import { parseOperator } from '../../../loads-sourcing/store/filter-util';
import { INPUT_TYPES } from '@shared/tabbed-table/constants';
import { ACCOUNT_TEAMS } from '../store/actions';
import { ACCOUNT_STATUS, ACCOUNT_TYPE, USER_ROLES } from '@domain/constants/app-variables';

export const USER_FIELDS = {
	FIRST_NAME: 'First Name',
	LAST_NAME: 'Last Name',
	EMAIL: 'Email',
	ACCOUNT_STATUS: 'Status',
	ROLE: 'Role',
	LAST_LOGIN: 'Last Login',
	PHONE_NUMBER: 'Phone Number',
};

export function buildHeaders() {
	let headers = [
		{ dataName: 'select', formattedName: 'select' },
		{ dataName: 'firstName', formattedName: USER_FIELDS.FIRST_NAME },
		{ dataName: 'lastName', formattedName: USER_FIELDS.LAST_NAME },
		{ dataName: 'email', formattedName: USER_FIELDS.EMAIL },
		{ dataName: 'accountStatus', formattedName: USER_FIELDS.ACCOUNT_STATUS },
		{ dataName: 'role', formattedName: USER_FIELDS.ROLE },
		{ dataName: 'lastLogin', formattedName: USER_FIELDS.LAST_LOGIN },
		{ dataName: 'phoneNumber', formattedName: USER_FIELDS.PHONE_NUMBER },
	];
	return headers;
}

export function buildHeaderDataTypes() {
	let types = ['string', 'string', 'string', 'string', 'string', 'date', 'phoneNumber'];
	return types;
}

export function buildDisplayedColumns(isSmallScreen: boolean) {
	let columns = [];
	if (isSmallScreen) {
		columns = ['select', 'firstName', 'accountStatus'];
	} else {
		columns = [
			'select',
			'firstName',
			'lastName',
			'email',
			'accountStatus',
			'role',
			'lastLogin',
			'phoneNumber',
		];
	}
	return columns;
}

export function buildActions(store, accountType?: string) {
	const actions = [
		{
			label: 'Invite Users',
			action: (payload) => {
				store.dispatch(ACCOUNT_TEAMS.INVITE_USERS.OPEN_DIALOG({}));
			},
			disabled: (selected) => {
				return false;
			},
		},
		{
			label: 'Activate User',
			action: (payload) => {
				payload.forEach((user) => {
					store.dispatch(ACCOUNT_TEAMS.ACTIVATE_USER.ACTIVATE({ user }));
				});
			},
			disabled: (selected) => {
				// Disable if no rows selected
				if (!selected || selected.length === 0) {
					return true;
				}
				// Disable if any selected row doesn't allow activate (only INACTIVE and PENDING allow activate)
				return selected.some(
					(user) =>
						user.accountStatus !== ACCOUNT_STATUS.INACTIVE &&
						user.accountStatus !== ACCOUNT_STATUS.PENDING
				);
			},
		},
		{
			label: 'Deactivate User',
			action: (payload) => {
				payload.forEach((user) => {
					store.dispatch(ACCOUNT_TEAMS.DEACTIVATE_USER.DEACTIVATE({ user }));
				});
			},
			disabled: (selected) => {
				// Disable if no rows selected
				if (!selected || selected.length === 0) {
					return true;
				}
				// Disable if any selected row doesn't allow deactivate (only ACTIVE and PENDING allow deactivate)
				return selected.some(
					(user) =>
						user.accountStatus !== ACCOUNT_STATUS.ACTIVE &&
						user.accountStatus !== ACCOUNT_STATUS.PENDING
				);
			},
		},
		{
			label: 'Add Admin Role',
			action: (payload) => {
				payload.forEach((user) => {
					store.dispatch(ACCOUNT_TEAMS.UPDATE_USER_ROLE.UPDATE({ action: 'ADD_ADMIN', user }));
				});
			},
			disabled: (selected) => {
				// Disable if no rows selected
				if (!selected || selected.length === 0) {
					return true;
				}
				// Disable if any selected row already has ADMIN role or is INACTIVE
				return selected.some(
					(user) =>
						user.role.indexOf(USER_ROLES.ADMIN) !== -1 ||
						user.accountStatus === ACCOUNT_STATUS.INACTIVE
				);
			},
		},
		{
			label: 'Remove Admin Role',
			action: (payload) => {
				payload.forEach((user) => {
					store.dispatch(ACCOUNT_TEAMS.UPDATE_USER_ROLE.UPDATE({ action: 'REMOVE_ADMIN', user }));
				});
			},
			disabled: (selected) => {
				// Disable if no rows selected
				if (!selected || selected.length === 0) {
					return true;
				}
				// Disable if any selected row doesn't have ADMIN role or is INACTIVE
				return selected.some(
					(user) =>
						user.role.indexOf(USER_ROLES.ADMIN) === -1 ||
						user.accountStatus === ACCOUNT_STATUS.INACTIVE
				);
			},
		},
		{
			label: 'Add CC Recipient Role',
			action: (payload) => {
				payload.forEach((user) => {
					store.dispatch(ACCOUNT_TEAMS.UPDATE_USER_ROLE.UPDATE({ action: 'ADD_CC_RECIPIENT', user }));
				});
			},
			disabled: (selected) => {
				// Disable if no rows selected
				if (!selected || selected.length === 0) {
					return true;
				}
				// Disable if any selected row already has CC_RECIPIENT role or is INACTIVE
				return selected.some(
					(user) =>
						user.role.indexOf(USER_ROLES.CC_RECIPIENT) !== -1 ||
						user.accountStatus === ACCOUNT_STATUS.INACTIVE
				);
			},
		},
		{
			label: 'Remove CC Recipient Role',
			action: (payload) => {
				payload.forEach((user) => {
					store.dispatch(ACCOUNT_TEAMS.UPDATE_USER_ROLE.UPDATE({ action: 'REMOVE_CC_RECIPIENT', user }));
				});
			},
			disabled: (selected) => {
				// Disable if no rows selected
				if (!selected || selected.length === 0) {
					return true;
				}
				// Disable if any selected row doesn't have CC_RECIPIENT role or is INACTIVE
				return selected.some(
					(user) =>
						user.role.indexOf(USER_ROLES.CC_RECIPIENT) === -1 ||
						user.accountStatus === ACCOUNT_STATUS.INACTIVE
				);
			},
		},
	];

	// Only add "Set Email Recipient" action if account type is CARRIER
	if (accountType === ACCOUNT_TYPE.CARRIER) {
		actions.push({
			label: 'Set Email Recipient',
			action: (payload) => {
				payload.forEach((user) => {
					store.dispatch(ACCOUNT_TEAMS.SET_EMAIL_RECIPIENT.SET({ user }));
				});
			},
			disabled: (selected) => {
				// Disable if no rows selected
				if (!selected || selected.length === 0) {
					return true;
				}
				return false;
			},
		});
	}

	return actions;
}

export const SORT_FIELDS = [
	USER_FIELDS.FIRST_NAME,
	USER_FIELDS.LAST_NAME,
	USER_FIELDS.EMAIL,
	USER_FIELDS.ACCOUNT_STATUS,
	USER_FIELDS.ROLE,
	USER_FIELDS.LAST_LOGIN,
	USER_FIELDS.PHONE_NUMBER,
];

export const FILTER_FIELDS = [
	USER_FIELDS.FIRST_NAME,
	USER_FIELDS.LAST_NAME,
	USER_FIELDS.EMAIL,
	USER_FIELDS.ACCOUNT_STATUS,
	USER_FIELDS.ROLE,
	USER_FIELDS.LAST_LOGIN,
	USER_FIELDS.PHONE_NUMBER,
];

export const USER_STATUS = [
	{ label: 'Pending', type: ACCOUNT_STATUS.PENDING },
	{ label: 'Active', type: ACCOUNT_STATUS.ACTIVE },
	{ label: 'Inactive', type: ACCOUNT_STATUS.INACTIVE },
];

export const USER_ROLE = [
	{ label: 'Owner', type: USER_ROLES.OWNER },
	{ label: 'Admin', type: USER_ROLES.ADMIN },
	{ label: 'User', type: USER_ROLES.USER },
	{ label: 'Email Editor', type: USER_ROLES.EMAIL_EDITOR },
	{ label: 'CC Recipient', type: USER_ROLES.CC_RECIPIENT },
];

export let filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();
filterFieldsInputTypeMap.set(USER_FIELDS.FIRST_NAME, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(USER_FIELDS.LAST_NAME, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(USER_FIELDS.EMAIL, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(USER_FIELDS.ACCOUNT_STATUS, INPUT_TYPES.USER_STATUS);
filterFieldsInputTypeMap.set(USER_FIELDS.ROLE, INPUT_TYPES.USER_ROLE);
filterFieldsInputTypeMap.set(USER_FIELDS.LAST_LOGIN, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(USER_FIELDS.PHONE_NUMBER, INPUT_TYPES.STRING);

export const SERVER_FILTER_MAP = {
	[USER_FIELDS.FIRST_NAME]: 'firstName',
	[USER_FIELDS.LAST_NAME]: 'lastName',
	[USER_FIELDS.EMAIL]: 'email',
	[USER_FIELDS.ACCOUNT_STATUS]: 'accountStatus',
	[USER_FIELDS.ROLE]: 'role',
	[USER_FIELDS.LAST_LOGIN]: 'lastLogin',
	[USER_FIELDS.PHONE_NUMBER]: 'phoneNumber',
};

export const SERVER_SORT_MAP = {
	[USER_FIELDS.FIRST_NAME]: 'firstName',
	[USER_FIELDS.LAST_NAME]: 'lastName',
	[USER_FIELDS.EMAIL]: 'email',
	[USER_FIELDS.ACCOUNT_STATUS]: 'accountStatus',
	[USER_FIELDS.ROLE]: 'role',
	[USER_FIELDS.LAST_LOGIN]: 'lastLogin',
	[USER_FIELDS.PHONE_NUMBER]: 'phoneNumber',
};

export const parseValue = (
	filterField: string,
	value: any,
	convertEquipmentsToUUID: boolean = true
) => {
	if (IS_NULL_OR_UNDEFINED(value)) {
		return null;
	}
	if (Array.isArray(value)) {
		return value.join(',');
	}
	return `${value}`;
};

export const mapFilterData = (filtersList: any[]): any[] => {
	if (IS_EMPTY_LIST(filtersList)) return [];
	return filtersList.map((filter) => ({
		field: SERVER_FILTER_MAP[filter.filterField],
		value: parseValue(filter.filterField, filter.filterText, false),
		condition: filter.filterCondition || 'and',
		operator: parseOperator(filter.filterField, filter.operator),
		startValue: parseValue(filter.filterField, filter.startValue),
		endValue: parseValue(filter.filterField, filter.endValue),
	}));
};

export const mapSortData = (sortList: any[]): any[] => {
	if (IS_EMPTY_LIST(sortList)) return [];
	return sortList.map((sort) => ({
		field: SERVER_SORT_MAP[sort.sortField],
		direction: sort.direction,
	}));
};
